<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :embedded="embedded"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">
    <div class="row no-gutters h-100">
      <div class="col-2 p-2">
        <AnalyticsDashboardValueRow>
          <div class="col">
            <ReportFilterPopover
              headerTitle="Task Scanned Out Items Filter"
              :showApplyButton="true"
              :showResetButton="true"
              :appendToBody="true"
              alignment="left"
              placement="bottom-end"
              @apply="applyTaskScannedFilter"
              @reset="resetTaskScannedFilter">
              <template v-slot:popover>
                <ReportDateRangeSelect 
                  :startDate="editingTaskScannedFilter.startDate"
                  :endDate="editingTaskScannedFilter.endDate"
                  @updateStartDate="editingTaskScannedFilter.startDate = $event"
                  @updateEndDate="editingTaskScannedFilter.endDate = $event"
                />
                <FormHorizontalRule />
                Task Type
                <FormMultiSelect :options="taskFilterTypeOptions" v-model="editingTaskScannedFilter.taskTypeIdList" />
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedTaskScannedFilter.startDate | formatDate}} - {{appliedTaskScannedFilter.endDate | formatDate}}
                </div>                
              </template>
            </ReportFilterPopover>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow v-if="isScannedQuantityType">
          <div class="col">
            <AnalyticsDashboardValue title="Total Scanned Quantity" highlight>
              {{formatDecimal(taskScannedItemData.scannedItemTotalQuantity, 1, true)}} item{{displayCountPlural(taskScannedItemData.scannedItemTotalQuantity)}}
              <template v-slot:info>Over {{taskScannedItemData.totalTaskCount}} task{{displayCountPlural(taskScannedItemData.totalTaskCount)}} during the period containing {{taskScannedItemData.activeTaskDayCount}} day{{displayCountPlural(taskScannedItemData.activeTaskDayCount)}} of task completions</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow v-if="isScannedAreaType">
          <div class="col">
            <AnalyticsDashboardValue title="Total Scanned Area" highlight>
              {{formatDecimal(taskScannedItemData.scannedItemTotalArea, 2, true)}} m&sup2;
              <template v-slot:info>Over {{taskScannedItemData.totalTaskCount}} task{{displayCountPlural(taskScannedItemData.totalTaskCount)}} during the period containing {{taskScannedItemData.activeTaskDayCount}} day{{displayCountPlural(taskScannedItemData.activeTaskDayCount)}} of task completions</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow v-if="isScannedQuantityType">
          <div class="col">
            <AnalyticsDashboardValue title="Average Daily Scanned Quantity">
              {{formatDecimal(taskScannedItemData.averageDailyScannedItemQuantity, 1, true)}} item{{displayCountPlural(taskScannedItemData.averageDailyScannedItemQuantity)}}
              <template v-slot:info>Over {{taskScannedItemData.activeTaskDayCount}} day{{displayCountPlural(taskScannedItemData.activeTaskDayCount)}} of task completions. With an average {{formatDecimal(taskScannedItemData.averageTaskScannedItemQuantity, 1, true)}} item{{displayCountPlural(taskScannedItemData.averageTaskScannedItemQuantity)}} per task</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow v-if="isScannedAreaType">
          <div class="col">
            <AnalyticsDashboardValue title="Average Daily Scanned Area">
              {{formatDecimal(taskScannedItemData.averageDailyScannedItemArea, 2, true)}} m&sup2;
              <template v-slot:info>Over {{taskScannedItemData.activeTaskDayCount}} day{{displayCountPlural(taskScannedItemData.activeTaskDayCount)}} of task completions. With an average {{formatDecimal(taskScannedItemData.averageTaskScannedItemArea, 2, true)}} m&sup2; per task</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

      </div>
      <div class="col-10">
        <ChartBar
          title="Daily Task Scanned Items"
          :series="taskScannedItemData.series"
          :xAxis="taskScannedItemData.xAxis"
          :yAxis="taskScannedItemData.yAxis"
          :legend="taskScannedItemData.legend"
          :options="taskScannedItemData.options"
          :popupEnabled="true"
          :exportEnabled="true"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import Utils from '@/utils/utils'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import ChartBar from '@/components/charts/chartBar'

const TaskDailyChartTypes = {
  scannedItemTaskTypeQuantity: 'scannedItemTaskTypeQuantity',
  scannedItemTaskTypeArea: 'scannedItemTaskTypeArea',
  scannedItemPrimaryUserQuantity: 'scannedItemPrimaryUserQuantity',
  scannedItemPrimaryUserArea: 'scannedItemPrimaryUserArea',
}

export default {
  name: 'TaskScannedItemDailyChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartBar,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: {
      type: String,
      default: TaskDailyChartTypes.scannedItemTaskTypeQuantity
    },
    filter: Object,
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  TaskDailyChartTypes: TaskDailyChartTypes,
  data() {
    return {
      taskScannedItemData: {
        scannedItemTotalArea: 0,
        scannedItemTotalQuantity: 0,
        totalTaskCount: 0,
        averageDailyScannedItemQuantity: 0,
        averageDailyScannedItemArea: 0,
        averageDailyTaskCount: 0,
        averageTaskScannedItemQuantity: 0,    // Per Task
        averageTaskScannedItemArea: 0,        // Per Task
        activeTaskDayCount: 0
      },
      taskScannedItemData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultTaskScannedFilter: {    
        startDate: moment().local().subtract(6, 'months').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString(),
        taskTypeIdList: []
      },
      editingTaskScannedFilter: {},
      appliedTaskScannedFilter: {},

      taskTypeList: [],
    }
  },
  computed: {
    taskFilterTypeOptions() {
      return this.taskTypeList.map(taskType => {
        return {
          value: taskType.id,
          name: taskType.name
        }
      })
    },
    isScannedQuantityType() {
      return this.type === TaskDailyChartTypes.scannedItemTaskTypeQuantity || this.type === TaskDailyChartTypes.scannedItemPrimaryUserQuantity
    },
    isScannedAreaType() {
      return this.type === TaskDailyChartTypes.scannedItemTaskTypeArea || this.type === TaskDailyChartTypes.scannedItemPrimaryUserArea
    }
  },
  mounted() {
    this.editingTaskScannedFilter = _.clone(this.defaultTaskScannedFilter)
    this.appliedTaskScannedFilter = _.clone(this.defaultTaskScannedFilter)

    this.requestData()
    this.requestChartData()
  },
  beforeDestroy() {
  },
  watch: {
    filter: function() {
      this.requestChartData()
    }
  },
  methods: {
    applyTaskScannedFilter() {
      this.appliedTaskScannedFilter = _.clone(this.editingTaskScannedFilter)

      this.requestChartData()
    },
    resetTaskScannedFilter() {
      this.editingTaskScannedFilter = _.clone(this.defaultTaskScannedFilter)
      this.appliedTaskScannedFilter = _.clone(this.defaultTaskScannedFilter)

      this.requestChartData()
    },
    requestData() {
      this.$apiService.tasksService.getTaskTypeList({ filter: { isActive: 1 }})
      .then(response => {
        this.taskTypeList = response.data
      })
      .catch(err => {
        console.error(err)
      })
    },
    requestChartData() {
      var taskScannedFilter = {
        type: 'daily',
        startDate: this.appliedTaskScannedFilter.startDate,
        endDate: this.appliedTaskScannedFilter.endDate,
        taskTypeIdList: this.appliedTaskScannedFilter.taskTypeIdList && this.appliedTaskScannedFilter.taskTypeIdList.length > 0 ? this.appliedTaskScannedFilter.taskTypeIdList : null,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null
      }

      let widgetComp = this

      if(this.type == TaskDailyChartTypes.scannedItemTaskTypeQuantity || this.type == TaskDailyChartTypes.scannedItemTaskTypeArea) {
        this.$apiService.analyticsService.getTasksScannedItemsTaskTypesChartData(taskScannedFilter)
        .then(response => {
          let chartData = response.data

          let taskTypeIdList = _.uniq(chartData.map(chartEntry => chartEntry.taskTypeId)).filter(taskTypeId => taskTypeId != null)
          let taskTypeName = _.uniq(chartData.map(chartEntry => chartEntry.taskTypeName))

          this.taskScannedItemData.legend = {
            data: [...taskTypeName]
          }
          this.taskScannedItemData.xAxis = {
            exportName: 'Date',
            exportData: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM YYYY')),
            data: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM YY'))
          }
          this.taskScannedItemData.yAxis = [
            {
              type: 'value',
              axisLabel: {
                formatter: (value) => {
                  return this.formatDecimal(value, 1) + ` Item${parseInt(value) == 1 ? '' : 's'}`
                }
              }
            },
            { type: 'value', alignTicks: true }
          ]
          this.taskScannedItemData.series = []

          taskTypeIdList.forEach(taskTypeId => {
            let seriesData = chartData.filter(chartEntry => chartEntry.taskTypeId === taskTypeId)
            if(seriesData.length === 0)
              return

            this.taskScannedItemData.series.push({
              type: 'bar',
              name: seriesData[0].taskTypeName,
              stack: 'scannedItems',
              data: seriesData.map(chartEntry => {

                if(this.isScannedAreaType) {
                  return [moment(chartEntry.date).format('DD MMM YY'), chartEntry.totalScannedOutArea]
                }

                return [moment(chartEntry.date).format('DD MMM YY'), chartEntry.totalScannedOutQuantity]
              }),
              tooltip: {
                valueFormatter(value) {
                  return widgetComp.formatDecimal(value, 1) + ` Item${parseInt(value) == 1 ? '' : 's'}`
                }
              }
            })
          })

          // Summary info
          let dayCount = _.uniq(chartData.map(chartEntry => chartEntry.date)).length
          this.taskScannedItemData.activeTaskDayCount = dayCount
          this.taskScannedItemData.scannedItemTotalArea = chartData.reduce((curValue, chartEntry) => curValue + chartEntry.totalScannedOutArea, 0)
          this.taskScannedItemData.scannedItemTotalQuantity = chartData.reduce((curValue, chartEntry) => curValue + chartEntry.totalScannedOutQuantity, 0)
          this.taskScannedItemData.totalTaskCount = chartData.reduce((curValue, chartEntry) => curValue + chartEntry.totalTaskCount, 0)

          this.taskScannedItemData.averageDailyScannedItemQuantity = 0
          if(dayCount > 0) {
            this.taskScannedItemData.averageDailyScannedItemQuantity = this.taskScannedItemData.scannedItemTotalQuantity / dayCount
            this.taskScannedItemData.averageDailyScannedItemArea = this.taskScannedItemData.scannedItemTotalArea / dayCount
            this.taskScannedItemData.averageDailyTaskCount = this.taskScannedItemData.totalTaskCount / dayCount
          } else {
            this.taskScannedItemData.averageDailyScannedItemQuantity = 0
            this.taskScannedItemData.averageDailyScannedItemArea = 0
            this.taskScannedItemData.averageDailyTaskCount = 0
          }

          if(this.taskScannedItemData.totalTaskCount > 0) {
            this.taskScannedItemData.averageTaskScannedItemQuantity = this.taskScannedItemData.scannedItemTotalQuantity / this.taskScannedItemData.totalTaskCount
            this.taskScannedItemData.averageTaskScannedItemArea = this.taskScannedItemData.scannedItemTotalArea / this.taskScannedItemData.totalTaskCount
          } else {
            this.taskScannedItemData.averageTaskScannedItemQuantity = 0
            this.taskScannedItemData.averageTaskScannedItemArea = 0
          }
        })
      }

      if(this.type == TaskDailyChartTypes.scannedItemPrimaryUserQuantity || this.type == TaskDailyChartTypes.scannedItemPrimaryUserArea) {
        this.$apiService.analyticsService.getTasksScannedItemsPrimaryUserChartData(taskScannedFilter)
        .then(response => {
          let chartData = response.data

          let primaryUserIdList = _.uniq(chartData.map(chartEntry => chartEntry.primaryUserId)).filter(primaryUserId => primaryUserId != null)
          let primaryUserNameList = _.uniq(chartData.map(chartEntry => chartEntry.primaryUserName))

          this.taskScannedItemData.legend = {
            data: [...primaryUserNameList]
          }
          this.taskScannedItemData.xAxis = {
            exportName: 'Date',
            exportData: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM YYYY')),
            data: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM YY'))
          }
          this.taskScannedItemData.yAxis = [
            {
              type: 'value',
              axisLabel: {
                formatter: (value) => {
                  return this.formatDecimal(value, 1) + ` Item${parseInt(value) == 1 ? '' : 's'}`
                }
              }
            },
            { type: 'value', alignTicks: true }
          ]
          this.taskScannedItemData.series = []

          primaryUserIdList.forEach(primaryUserId => {
            let seriesData = chartData.filter(chartEntry => chartEntry.primaryUserId === primaryUserId)
            if(seriesData.length === 0)
              return

            this.taskScannedItemData.series.push({
              type: 'bar',
              name: seriesData[0].primaryUserName,
              stack: 'scannedItems',
              data: seriesData.map(chartEntry => {

                if(this.isScannedAreaType) {
                  return [moment(chartEntry.date).format('DD MMM YY'), chartEntry.totalScannedOutArea]
                }

                return [moment(chartEntry.date).format('DD MMM YY'), chartEntry.totalScannedOutQuantity]
              }),
              tooltip: {
                valueFormatter(value) {
                  return widgetComp.formatDecimal(value, 1) + ` Item${parseInt(value) == 1 ? '' : 's'}`
                }
              }
            })
          })

          // Summary info
          let dayCount = _.uniq(chartData.map(chartEntry => chartEntry.date)).length
          this.taskScannedItemData.activeTaskDayCount = dayCount
          this.taskScannedItemData.scannedItemTotalArea = chartData.reduce((curValue, chartEntry) => curValue + chartEntry.totalScannedOutArea, 0)
          this.taskScannedItemData.scannedItemTotalQuantity = chartData.reduce((curValue, chartEntry) => curValue + chartEntry.totalScannedOutQuantity, 0)
          this.taskScannedItemData.totalTaskCount = chartData.reduce((curValue, chartEntry) => curValue + chartEntry.totalTaskCount, 0)

          this.taskScannedItemData.averageDailyScannedItemQuantity = 0
          if(dayCount > 0) {
            this.taskScannedItemData.averageDailyScannedItemQuantity = this.taskScannedItemData.scannedItemTotalQuantity / dayCount
            this.taskScannedItemData.averageDailyScannedItemArea = this.taskScannedItemData.scannedItemTotalArea / dayCount
            this.taskScannedItemData.averageDailyTaskCount = this.taskScannedItemData.totalTaskCount / dayCount
          } else {
            this.taskScannedItemData.averageDailyScannedItemQuantity = 0
            this.taskScannedItemData.averageDailyScannedItemArea = 0
            this.taskScannedItemData.averageDailyTaskCount = 0
          }

          if(this.taskScannedItemData.totalTaskCount > 0) {
            this.taskScannedItemData.averageTaskScannedItemQuantity = this.taskScannedItemData.scannedItemTotalQuantity / this.taskScannedItemData.totalTaskCount
            this.taskScannedItemData.averageTaskScannedItemArea = this.taskScannedItemData.scannedItemTotalArea / this.taskScannedItemData.totalTaskCount
          } else {
            this.taskScannedItemData.averageTaskScannedItemQuantity = 0
            this.taskScannedItemData.averageTaskScannedItemArea = 0
          }
        })
      }
    },
  }
}
</script>